import React, { useState, useEffect } from 'react';
import { Mail, Sparkles } from 'lucide-react';
import './Login.css';
import { AuthErrorBanner } from './AuthErrorBanner';
import './AuthErrorBanner.css';
import { apiGet, apiPost } from '../lib/api';
import { isNative, setStoredToken } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function Login({ onLogin }) {
  const [mode, setMode] = useState('options'); // options, email, magic
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Check for magic link token on mount. The native deep-link listener
  // (App.js > NativeDeepLinkBridge) routes magic-link taps to /?token=...
  // so this same path covers both web and native; we don't register a
  // second listener here to avoid double-verifying the token.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      window.history.replaceState({}, '', window.location.pathname);
      verifyMagicLink(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Persist session_token (native only) — extracted so every successful
   *  auth path can call it without scattering branching. */
  const persistNativeToken = async (data) => {
    if (isNative() && data && data.session_token) {
      await setStoredToken(data.session_token);
    }
  };

  const verifyMagicLink = async (token) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await apiGet(`/api/auth/magic-link/verify?token=${token}`);

      if (response.ok) {
        const userData = await response.json();
        await persistNativeToken(userData);
        onLogin(userData);
      } else {
        // Surface the backend's `detail` when it's a string; otherwise
        // fall back to a generic message.
        let detailMsg = null;
        try {
          const data = await response.json();
          if (typeof data?.detail === 'string' && data.detail.trim()) {
            detailMsg = data.detail.trim();
          }
        } catch (_) { /* ignore */ }
        setError(detailMsg || 'Invalid or expired magic link');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (isNative() && !BACKEND_URL) {
      setError('App is misconfigured. Please contact support.');
      return;
    }
    // Derive return_to from stored intended destination or default to /app
    let returnTo = sessionStorage.getItem('intendedDestination') || '/app';

    // Safety: must start with "/", must not contain "://" or start with "//"
    if (!returnTo || !returnTo.startsWith('/') || returnTo.includes('://') || returnTo.startsWith('//')) {
      returnTo = '/app';
    }

    if (isNative()) {
      // Open the OAuth start URL in the in-app browser. The backend will
      // redirect to com.hourtrix.app://oauth/callback?token=... which the
      // App-level NativeDeepLinkBridge catches.
      const url = `${BACKEND_URL}/api/auth/google/oauth/start?return_to=${encodeURIComponent(returnTo)}&client=mobile`;
      try {
        // Dynamic import keeps @capacitor/browser out of the web bundle.
        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url });
      } catch (e) {
        setError('Could not open Google sign-in. Please try again.');
      }
      return;
    }

    // Web: top-level navigation (cookie-based session).
    window.location.assign(`/api/auth/google/oauth/start?return_to=${encodeURIComponent(returnTo)}`);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) { setError('Enter a valid email'); return; }
    if (isSignup && (!name || !name.trim())) { setError('Name is required'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setIsLoading(true);

    try {
      const endpoint = isSignup ? '/api/auth/email/signup' : '/api/auth/email/login';
      const body = isSignup 
        ? { email, password, name }
        : { email, password };

      const response = await apiPost(endpoint, body);

      if (response.ok) {
        const userData = await response.json();
        await persistNativeToken(userData);
        onLogin(userData);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Authentication failed');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const body = isNative() ? { email, client: 'mobile' } : { email };
      const response = await apiPost('/api/auth/magic-link/request', body);
      if (response.ok) {
        // Email-sent path. Render an inline success panel — alert() blocks
        // the WebView on native and is generally jarring UX.
        setMagicLinkSent(true);
      } else {
        setError('Failed to send magic link');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-container">
        <div className="login-header">
          <h1>HOURTRIX</h1>
          <p className="login-subtitle">AI-Powered Time Budget Tracker</p>
        </div>

        <AuthErrorBanner variant="login" />

        {mode === 'options' && (
          <div className="login-options">
            <button className="login-btn login-google" onClick={handleGoogleLogin}>
              <svg className="login-icon" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 6.293C4.672 4.169 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>
            
            <div className="divider">
              <span>or</span>
            </div>

            <button className="login-btn login-email" onClick={() => setMode('email')}>
              <Mail className="login-icon" size={18} aria-hidden="true" />
              Continue with Email
            </button>

            <button className="login-btn login-magic" onClick={() => setMode('magic')}>
              <Sparkles className="login-icon" size={18} aria-hidden="true" />
              Send Magic Link
            </button>
          </div>
        )}

        {mode === 'email' && (
          <div className="login-form">
            <form onSubmit={handleEmailAuth}>
              {isSignup && (
                <input
                  type="text"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="login-input"
                  autoComplete="name"
                />
              )}
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-input"
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                minLength={8}
                autoComplete={isSignup ? 'new-password' : 'current-password'}
              />
              {isSignup && (
                <p className="login-help">
                  At least 8 characters, including a letter and a digit.
                </p>
              )}
              
              {error && <div className="login-error">{error}</div>}
              
              <button type="submit" className="login-submit" disabled={isLoading}>
                {isLoading ? 'Please wait...' : (isSignup ? 'Sign Up' : 'Log In')}
              </button>
            </form>

            <button className="login-toggle" onClick={() => setIsSignup(!isSignup)}>
              {isSignup ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
            </button>

            <button className="login-back" onClick={() => setMode('options')}>
              ← Back
            </button>
          </div>
        )}

        {mode === 'magic' && (
          <div className="login-form">
            {magicLinkSent ? (
              <div className="login-magic-sent" role="status" data-testid="magic-link-sent">
                <div className="login-magic-icon" aria-hidden="true">✉️</div>
                <h2 className="login-magic-title">Check your email</h2>
                <p className="login-magic-body">
                  We sent a sign-in link to <strong>{email}</strong>.
                  Open the email and tap the link to finish signing in.
                </p>
                <p className="login-magic-help">
                  Link expires in 15 minutes. Didn't get it? Check spam, or
                  try again with a different address.
                </p>
                <button
                  className="login-back"
                  onClick={() => {
                    setMagicLinkSent(false);
                    setEmail('');
                    setMode('options');
                  }}
                >
                  ← Back to sign-in options
                </button>
              </div>
            ) : (
              <>
                <form onSubmit={handleMagicLink}>
                  <p className="magic-info">Enter your email to receive a magic link</p>
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="login-input"
                    autoComplete="email"
                  />
                  
                  {error && <div className="login-error">{error}</div>}
                  
                  <button type="submit" className="login-submit" disabled={isLoading}>
                    {isLoading ? 'Sending...' : 'Send Magic Link'}
                  </button>
                </form>

                <button className="login-back" onClick={() => setMode('options')}>
                  ← Back
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
